// prediction/wizards/SetupWizardSteps.jsx
import React from 'react';
import {
  Box,
  Typography,
  TextField,
  Alert,
  Card,
  CardContent,
  FormControlLabel,
  Switch,
  Divider
} from '@mui/material';
import TankIcon from '@mui/icons-material/Water';
import TimelineIcon from '@mui/icons-material/Timeline';

/**
 * タンク設置確認ステップ
 */
export const TankSetupStep = ({ 
  tankSetupChoice, 
  onTankSetupChange, 
  errors 
}) => (
  <Box>
    <Typography variant="h6" gutterBottom>
      🛢️ タンク設置状況を確認
    </Typography>
    
    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
      お客様先の設置状況を教えてください
    </Typography>

    <Card variant="outlined" sx={{ mb: 2 }}>
      <CardContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={tankSetupChoice === true}
                onChange={(e) => onTankSetupChange(e.target.checked ? true : null)}
                color="primary"
              />
            }
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TankIcon color="primary" />
                <Typography>タンクを設置している</Typography>
              </Box>
            }
          />
          
          <Divider />
          
          <FormControlLabel
            control={
              <Switch
                checked={tankSetupChoice === false}
                onChange={(e) => onTankSetupChange(e.target.checked ? false : null)}
                color="info"
              />
            }
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TimelineIcon color="info" />
                <Typography>タンクは未設置（予測機能のみ利用）</Typography>
              </Box>
            }
          />
        </Box>
      </CardContent>
    </Card>

    {tankSetupChoice === true && (
      <Alert severity="success">
        <Typography variant="body2">
          タンク容量、消費量、現在の残量を設定します
        </Typography>
      </Alert>
    )}

    {tankSetupChoice === false && (
      <Alert severity="info">
        <Typography variant="body2">
          消費量のみ設定して予測機能を利用します
        </Typography>
      </Alert>
    )}

    {errors.tankSetup && (
      <Alert severity="error" sx={{ mt: 2 }}>
        {errors.tankSetup}
      </Alert>
    )}
  </Box>
);

/**
 * 🚀 修正：タンク容量設定ステップ - 不要な文言削除
 */
export const CapacityStep = ({ 
  formData, 
  onInputChange, 
  errors 
}) => (
  <Box>
    <Typography variant="h6" gutterBottom>
      🛢️ タンク容量を入力
    </Typography>
    
    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
      満タン時の容量を入力してください
    </Typography>

    <TextField
      fullWidth
      label="タンク容量 (L)"
      type="text"
      inputProps={{ 
        inputMode: "numeric",
        pattern: "[0-9]*"
      }}
      value={formData.totalCapacity}
      onChange={(e) => onInputChange('totalCapacity', e.target.value)}
      error={!!errors.totalCapacity}
      helperText={errors.totalCapacity || "例: 1000"}
      placeholder="1000"
      size="large"
      sx={{ mb: 2 }}
    />
  </Box>
);

/**
 * 消費量設定ステップ
 */
export const ConsumptionStep = ({ 
  formData, 
  onInputChange, 
  errors,
  isRequired = false
}) => (
  <Box>
    <Typography variant="h6" gutterBottom>
      📊 月間消費量を入力
    </Typography>
    
    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
      {isRequired ? 
        '予測機能に必要な消費量を入力してください' : 
        '1ヶ月でどのくらい使うか入力してください'
      }
    </Typography>

    <TextField
      fullWidth
      label={`月間消費量 (L)${isRequired ? ' - 必須' : ''}`}
      type="text"
      inputProps={{ 
        inputMode: "numeric",
        pattern: "[0-9]*"
      }}
      value={formData.initialConsumptionRate}
      onChange={(e) => onInputChange('initialConsumptionRate', e.target.value)}
      error={!!errors.initialConsumptionRate}
      helperText={errors.initialConsumptionRate || (isRequired ? "予測機能に必要です" : "後から調整できます")}
      placeholder="100"
      size="large"
      sx={{ mb: 2 }}
    />

    <Card variant="outlined" sx={{ bgcolor: isRequired ? 'warning.50' : 'info.50', borderColor: isRequired ? 'warning.200' : 'info.200' }}>
      <CardContent sx={{ py: 2 }}>
        <Typography variant="body2" color={isRequired ? 'warning.dark' : 'info.dark'}>
          💡 過去の補充記録を参考にしてください
        </Typography>
      </CardContent>
    </Card>

    {isRequired && (
      <Alert severity="warning" sx={{ mt: 2 }}>
        <Typography variant="body2">
          <strong>注意：</strong>タンク未設置の場合、消費量は予測機能に必須です
        </Typography>
      </Alert>
    )}
  </Box>
);

/**
 * 実測残量設定ステップ
 */
export const RemainingStep = ({ 
  formData, 
  onInputChange, 
  onDateChange,
  errors 
}) => (
  <Box>
    <Typography variant="h6" gutterBottom>
      📏 現在の残量を入力
    </Typography>
    
    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
      今どのくらい残っているか入力してください
    </Typography>

    <Box display="flex" gap={2} sx={{ mb: 2 }}>
      <TextField
        fullWidth
        label="現在の残量 (L)"
        type="text"
        inputProps={{ 
          inputMode: "numeric",
          pattern: "[0-9]*",
          min: "0",
          max: formData.totalCapacity || "999999"
        }}
        value={formData.actualRemaining}
        onChange={(e) => onInputChange('actualRemaining', e.target.value)}
        error={!!errors.actualRemaining}
        helperText={errors.actualRemaining || 
          (formData.totalCapacity ? `タンク容量: ${formData.totalCapacity}L` : "分からない場合は空欄でも大丈夫です")
        }
        placeholder="300"
        size="large"
      />
      <TextField
        fullWidth
        label="確認日"
        type="date"
        inputProps={{ 
          max: new Date().toISOString().split('T')[0]
        }}
        value={formData.measuredDate}
        onChange={(e) => onDateChange(e.target.value)}
        error={!!errors.measuredDate}
        helperText={errors.measuredDate}
        InputLabelProps={{ shrink: true }}
        size="large"
      />
    </Box>

    <Card variant="outlined" sx={{ bgcolor: 'success.50', borderColor: 'success.200' }}>
      <CardContent sx={{ py: 2 }}>
        <Typography variant="body2" color="success.dark">
          💡 分からない場合は空欄でもOK
        </Typography>
      </CardContent>
    </Card>

    {formData.totalCapacity && formData.actualRemaining && 
     !errors.actualRemaining && !errors.totalCapacity && (
      <Alert severity="info" sx={{ mt: 2 }}>
        <Typography variant="body2">
          残量: {Math.round((parseInt(formData.actualRemaining, 10) / parseInt(formData.totalCapacity, 10)) * 100)}%
        </Typography>
      </Alert>
    )}
  </Box>
);

/**
 * ステップコンテンツのレンダリング
 */
export const renderStepContent = (
  step,
  formData,
  tankSetupChoice,
  onTankSetupChange,
  onInputChange,
  onDateChange,
  errors,
  showTankSetupStep,
  selectedProduct
) => {
  switch (step.id) {
    case 'tank_setup':
      return (
        <TankSetupStep
          tankSetupChoice={tankSetupChoice}
          onTankSetupChange={onTankSetupChange}
          errors={errors}
        />
      );

    case 'capacity':
      return (
        <CapacityStep
          formData={formData}
          onInputChange={onInputChange}
          errors={errors}
        />
      );

    case 'consumption':
      const hasTank = showTankSetupStep ? tankSetupChoice : Boolean(selectedProduct?.tankInstalled);
      const isConsumptionRequired = !hasTank;
      
      return (
        <ConsumptionStep
          formData={formData}
          onInputChange={onInputChange}
          errors={errors}
          isRequired={isConsumptionRequired}
        />
      );

    case 'remaining':
      return (
        <RemainingStep
          formData={formData}
          onInputChange={onInputChange}
          onDateChange={onDateChange}
          errors={errors}
        />
      );

    default:
      return null;
  }
};